import { formatMeasurement } from './fraction';
import { forWhom } from './orderFor';

const esc = (v) => String(v ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const dmy = (v) => {
  const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
};

/**
 * The measurement sheet a tailor works from, in the classic ruled-slip form:
 * shop name on top, a header block (bill no, customer, item, qty and the three
 * dates), then one row per measurement — name, value, and the note written
 * against it — and the price at the foot.
 *
 * Printed from its own window, so none of the app's layout or dark theme can
 * leak into it. Sized for A5; A4 prints the same sheet with wider margins.
 */
export function printMeasurementSlip({ company, order, line, metricName }) {
  const rows = (line.measurements || [])
    .map((m) => ({ name: metricName(m) || '', value: formatMeasurement(m.value), notes: m.notes || '' }))
    .filter((r) => r.value !== '' || r.notes);

  // No money on this sheet: it goes to the tailor, not the customer.
  const qty = Number(line.qty) || 1;

  // Keep the sheet looking like a ruled book even when little is filled in.
  const blanks = Math.max(0, 8 - rows.length);
  const body = [
    ...rows.map((r) => `<tr>
        <td class="nm">${esc(r.name)}</td>
        <td class="vl">${esc(r.value)}</td>
        <td class="nt">${esc(r.notes)}</td>
      </tr>`),
    ...Array.from({ length: blanks }, () => '<tr><td class="nm">&nbsp;</td><td class="vl"></td><td class="nt"></td></tr>'),
  ].join('');

  const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>${esc(line.itemName)} — measurement sheet</title>
<style>
  @page { size: A5 portrait; margin: 8mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', system-ui, sans-serif; color: #000; margin: 0 auto; max-width: 128mm; font-size: 11px; }
  .sheet { border: 1.5px solid #000; }
  .top { display: flex; justify-content: space-between; align-items: baseline; gap: 10px;
         padding: 5px 7px; border-bottom: 1.5px solid #000; }
  .top h1 { margin: 0; font-size: 14px; text-transform: uppercase; letter-spacing: .4px; }
  .top .r { font-size: 12px; font-weight: 700; white-space: nowrap; }
  .co { padding: 2px 7px 4px; font-size: 8.5px; color: #333; border-bottom: 1.5px solid #000; line-height: 1.4; }
  .hdr { width: 100%; border-collapse: collapse; }
  .hdr td { border-bottom: 1px solid #000; padding: 3px 7px; font-size: 10.5px; }
  .hdr td.k { width: 22mm; color: #333; }
  .hdr td.rt { text-align: right; white-space: nowrap; }
  .hdr tr:last-child td { border-bottom: 1.5px solid #000; }
  b.val { font-size: 11.5px; }
  table.ms { width: 100%; border-collapse: collapse; }
  table.ms th { font-size: 8px; text-transform: uppercase; letter-spacing: .7px; background: #f0f0f0;
                border-bottom: 1px solid #000; padding: 3px 7px; text-align: left; }
  table.ms td { border-bottom: 1px solid #ccc; padding: 4px 7px; height: 19px; }
  td.nm { font-size: 11px; }
  td.vl { width: 26mm; text-align: center; font-size: 13px; font-weight: 700; border-left: 1px solid #000; border-right: 1px solid #000; }
  td.nt { width: 46mm; font-size: 10px; }
  /* The tailor reads this as carefully as the numbers — so it is not fine print. */
  .remark { border-top: 1.5px solid #000; padding: 4px 7px 6px; }
  .remark span { display: block; font-size: 8px; text-transform: uppercase; letter-spacing: .7px; color: #333; }
  .remark p { margin: 2px 0 0; font-size: 12px; font-weight: 700; line-height: 1.35; }
  .foot { display: flex; border-top: 1.5px solid #000; }
  .terms { flex: 1; padding: 5px 7px; font-size: 8.5px; color: #333; }
  .sums { width: 48mm; border-left: 1px solid #000; }
  .sums div { display: flex; justify-content: space-between; padding: 3px 7px; font-size: 10.5px; border-bottom: 1px solid #ddd; }
  .sums .tot { font-weight: 700; font-size: 12px; background: #f0f0f0; border-bottom: 1px solid #000; }
  .thanks { text-align: center; font-size: 8.5px; letter-spacing: 3px; text-transform: uppercase; padding: 4px; border-top: 1.5px solid #000; }
</style></head>
<body>
  <div class="sheet">
    <div class="top">
      <h1>${esc(company?.name || 'Tailoring Shop')}</h1>
      <span class="r">Measurement Sheet</span>
    </div>
    ${company?.phone || company?.address || company?.gstin ? `<div class="co">
      ${[company?.address, company?.phone && `Ph: ${company.phone}`, company?.email, company?.gstin && `GSTIN: ${company.gstin}`]
    .filter(Boolean).map(esc).join(' · ')}
    </div>` : ''}

    <table class="hdr">
      <tr>
        <td class="k">Bill No.</td><td><b class="val">${esc(order?.orderNo || '—')}</b></td>
        <td class="rt">Order Dt. : <b>${dmy(order?.orderDate) || '—'}</b></td>
      </tr>
      <tr>
        <td class="k">Customer</td><td><b class="val">${esc(order?.customerName || '—')}</b>${order?.customerMobile ? ` · ${esc(order.customerMobile)}` : ''}${forWhom(order?.familyMember || (order?.familyMemberName ? { name: order.familyMemberName, relation: order.familyMemberRelation } : null)) ? ` <b class="val">(${esc(forWhom(order.familyMember || { name: order.familyMemberName, relation: order.familyMemberRelation }))})</b>` : ''}</td>
        <td class="rt">Trial Dt. : <b>${dmy(order?.trialDate) || '—'}</b></td>
      </tr>
      <tr>
        <td class="k">Item Name</td><td><b class="val">${esc(line.itemName)}</b>${line.alteration ? ' (Alteration)' : ''}</td>
        <td class="rt">Deli. Dt. : <b>${dmy(order?.deliveryDate) || '—'}</b></td>
      </tr>
      <tr>
        <td class="k">Qty.</td><td colspan="2"><b class="val">${qty}</b></td>
      </tr>
    </table>

    <table class="ms">
      <thead><tr><th>Measurement</th><th style="text-align:center">Value</th><th>Notes</th></tr></thead>
      <tbody>${body}</tbody>
    </table>

    ${line.remarks ? `<div class="remark"><span>Remark / Style</span><p>${esc(line.remarks)}</p></div>` : ''}

    <div class="foot">
      <div class="terms">
        Please check the measurements at the time of trial.<br>
        Goods not collected within 30 days are at the customer's risk.
      </div>
      <div class="sums">
        <div class="tot"><span>Pieces</span><span>${qty}</span></div>
        <div><span>Cut by</span><span></span></div>
        <div><span>Stitched by</span><span></span></div>
      </div>
    </div>
    <div class="thanks">Thanks — visit again</div>
  </div>
  <script>window.onload = function () { window.print(); };<\/script>
</body></html>`;

  const w = window.open('', '_blank', 'width=560,height=760');
  if (!w) return false; // pop-up blocked — caller tells the user
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}
